import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useOpenInvoiceCount } from "../api/openInvoices";
import {
  CART_STATUS_LABELS,
  cartsQueryKey,
  fetchCarts,
  type CartHeader,
} from "../api/carts";
import { listImportJobs, type ImportJob } from "../api/imports";
import { useSession } from "../auth/useSession";
import { isDemoMode } from "../lib/env";
import { formatDateShort, formatDateTimeShort } from "../lib/format";
import { droppedImportFiles, importDocTypeLabel, safeDisplayName } from "../lib/importFiles";
import { dataContextGuard } from "../lib/dataContext";
import { handOffUploadFiles } from "../lib/uploadHandoff";
import {
  recentlyViewedHref,
  useRecentlyViewed,
  type RecentlyViewedEntry,
} from "../lib/recentlyViewed";

// Startseite (#631): für alle gleich aufgebaut, gezeigt wird nur, was die
// Person selbst gerade braucht. Kennzahlen und Verläufe stehen in der
// Preis-Analyse; hier gibt es Suche, offene Schritte und die eigenen Vorgänge.
// Was jemand nicht tun darf, erscheint nicht – die Seite bleibt dieselbe.

const PANEL_ROWS = 5;

export default function CockpitPage() {
  const membership = useSession().user?.active_membership;
  const demo = isDemoMode();
  const canReviewPrices = membership?.can_review_prices !== false;
  const canUpload = !demo && membership?.can_upload_documents !== false;
  const canOpenImports = canUpload;

  const priceReviewQuery = useOpenInvoiceCount(canReviewPrices);
  // Seit dem Vier-Augen-Prinzip entscheidet Controlling über unsicher
  // erkannte Importe (#557).
  const pendingImportsQuery = useQuery({
    queryKey: ["imports", "jobs", "cockpit"],
    queryFn: () => listImportJobs(),
    enabled: canReviewPrices && !demo,
    retry: false,
    staleTime: 60_000,
  });
  const cartsQuery = useQuery({
    queryKey: cartsQueryKey("mine"),
    queryFn: () => fetchCarts("mine"),
    enabled: !demo,
    staleTime: 60_000,
  });
  const uploadsQuery = useQuery({
    queryKey: ["imports", "jobs", "mine"],
    queryFn: () => listImportJobs({ mine: true, limit: PANEL_ROWS }),
    enabled: canUpload,
    retry: false,
    staleTime: 30_000,
  });
  const recentlyViewed = useRecentlyViewed();

  const openPriceReviews = priceReviewQuery.count;
  const pendingImports = (pendingImportsQuery.data ?? []).filter(
    (job) => job.status === "review",
  ).length;

  const tasks: Task[] = [];
  if (canReviewPrices && openPriceReviews > 0) {
    tasks.push({
      key: "price-reviews",
      count: openPriceReviews,
      label: openPriceReviews === 1 ? "Rechnung prüfen" : "Rechnungen prüfen",
      hint: "mit Preisabweichungen über der Referenz",
      to: "/rechnungspruefung",
    });
  }
  if (canReviewPrices && pendingImports > 0) {
    tasks.push({
      key: "pending-imports",
      count: pendingImports,
      label: pendingImports === 1 ? "Import unsicher erkannt" : "Importe unsicher erkannt",
      hint: "übernehmen, korrigieren oder verwerfen",
      to: "/imports/verlauf",
    });
  }
  // Auftragsbestätigungen zählen hier bewusst nicht: der Bestand ist der
  // gesamte nie geprüfte Altbestand; ein Konzept dafür folgt nach der Beta.
  const tasksLoading =
    canReviewPrices && (priceReviewQuery.isLoading || (!demo && pendingImportsQuery.isLoading));
  // Ein fehlgeschlagener Abruf ist kein „Alles erledigt“.
  const tasksIncomplete =
    canReviewPrices && (priceReviewQuery.isError || (!demo && pendingImportsQuery.isError));

  return (
    <main className="mx-auto max-w-7xl px-6 py-6">
      <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Übersicht</h1>

      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
        {canUpload ? <UploadCard /> : null}
        <TasksCard tasks={tasks} isLoading={tasksLoading} isIncomplete={tasksIncomplete} />
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-3">
        {demo ? null : (
          <CartsPanel
            items={cartsQuery.data?.items ?? []}
            isLoading={cartsQuery.isLoading}
            isError={cartsQuery.isError}
          />
        )}
        {canUpload ? (
          <UploadsPanel
            items={uploadsQuery.data ?? []}
            isLoading={uploadsQuery.isLoading}
            isError={uploadsQuery.isError}
            linkToImports={canOpenImports}
          />
        ) : null}
        <RecentlyViewedPanel items={recentlyViewed} />
      </div>
    </main>
  );
}

/**
 * Belege direkt von der Übersicht hochladen (#630): ablegen oder wählen, die
 * Dateien gehen in die Warteschlange von „Belege hochladen“.
 */
function UploadCard() {
  const navigate = useNavigate();
  const input = useRef<HTMLInputElement | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState("");
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const handOff = (files: File[]) => {
    if (!files.length) return;
    if (handOffUploadFiles(files)) navigate("/imports");
  };
  // Ordner werden asynchron gelesen: das Ergebnis gilt nur für denselben
  // Kontext und solange die Karte noch angezeigt wird (#632-Review R1).
  const handOffLater = (pending: Promise<File[]>) => {
    const sameContext = dataContextGuard();
    pending.then((files) => {
      sameContext();
      if (mounted.current) handOff(files);
    }).catch((reason: unknown) => {
      if (reason instanceof DOMException && reason.name === "AbortError") return;
      if (mounted.current) setError(reason instanceof Error ? reason.message : "Die Dateien konnten nicht gelesen werden.");
    });
  };
  return (
    <section
      aria-labelledby="cockpit-upload-heading"
      className={`flex flex-col rounded-xl border bg-white p-5 shadow-sm transition-colors ${dragActive ? "border-emerald-500 bg-emerald-50" : "border-slate-200"}`}
      onDragOver={(event) => {
        if (!event.dataTransfer.types.includes("Files")) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
        setDragActive(true);
      }}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragActive(false);
      }}
      onDrop={(event) => {
        event.preventDefault();
        setDragActive(false);
        handOffLater(droppedImportFiles(event.dataTransfer));
      }}
    >
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 border-b border-slate-100 pb-3">
        <h2 id="cockpit-upload-heading" className="text-lg font-semibold tracking-tight text-slate-900">Belege hochladen</h2>
        <Link to="/imports/verlauf" className="shrink-0 text-sm font-medium text-emerald-700 hover:underline">Zu den Importen →</Link>
      </div>
      <div className={`flex flex-1 flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-6 text-center ${dragActive ? "border-emerald-500" : "border-slate-300 bg-slate-50"}`}>
        <UploadGlyph />
        <p className="mt-2 text-sm font-medium text-slate-900">{dragActive ? "Dateien hier loslassen" : "Belege hierher ziehen"}</p>
        <p className="mt-1 text-xs text-slate-600">Rechnung, Gutschrift, Angebot oder Auftragsbestätigung – die Art wird erkannt.</p>
        <button
          type="button"
          onClick={() => input.current?.click()}
          className="mt-4 inline-flex h-9 items-center rounded-md bg-emerald-600 px-4 text-sm font-medium text-white shadow-sm hover:bg-emerald-700"
        >
          Dateien wählen
        </button>
        <input
          ref={input}
          type="file"
          multiple
          className="hidden"
          aria-label="Belege auswählen"
          onChange={(event) => {
            handOff(Array.from(event.currentTarget.files ?? []));
            event.currentTarget.value = "";
          }}
        />
      </div>
      {/* Massenimporte gehören auf die Upload-Seite mit Fortschritt und Ergebnisliste. */}
      <p className="mt-3 text-xs text-slate-600">
        Viele Dateien, Ordner oder ZIP?{" "}
        <Link to="/imports" className="font-medium text-emerald-700 hover:underline">Massenimport öffnen →</Link>
      </p>
      {error ? <p role="alert" className="mt-2 text-sm text-rose-700">{error}</p> : null}
    </section>
  );
}

function UploadGlyph() {
  return (
    <svg aria-hidden="true" className="h-8 w-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 16V4m0 0L7 9m5-5 5 5M4 16v4h16v-4" />
    </svg>
  );
}

type Task = {
  key: string;
  count: number;
  label: string;
  hint: string;
  to: string;
};

/**
 * Nur Schritte, die jetzt anstehen und die man selbst erledigen darf – als
 * ruhige Liste, nicht als Warnung. Ist nichts offen, steht dort ein Häkchen.
 */
function TasksCard({
  tasks,
  isLoading,
  isIncomplete,
}: {
  tasks: Task[];
  isLoading: boolean;
  isIncomplete: boolean;
}) {
  return (
    <Panel title="Zu erledigen">
      {tasks.length > 0 ? (
        <ul className="divide-y divide-slate-100">
          {tasks.map((task) => (
            <li key={task.key}>
              <RowLink to={task.to}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900">{task.label}</div>
                  <div className="mt-0.5 text-xs text-slate-600">{task.hint}</div>
                </div>
                <span className="flex shrink-0 items-center gap-2">
                  <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-slate-800">
                    {task.count.toLocaleString("de-AT")}
                  </span>
                  <span aria-hidden="true" className="text-slate-400">›</span>
                </span>
              </RowLink>
            </li>
          ))}
        </ul>
      ) : isLoading ? (
        <LoadingRows rows={3} />
      ) : isIncomplete ? null : (
        <p className="flex items-center gap-2 py-4 text-sm text-slate-600">
          <svg aria-hidden="true" className="h-5 w-5 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 5 5L20 7" /></svg>
          Alles erledigt – gerade wartet nichts auf dich.
        </p>
      )}
      {isIncomplete && !isLoading ? (
        <p className="mt-2 text-sm text-rose-700">Offene Schritte konnten nicht vollständig geladen werden.</p>
      ) : null}
    </Panel>
  );
}

const CART_STATUS_ORDER: Record<CartHeader["status"], number> = {
  open: 0,
  requested: 1,
  ordered: 2,
  done: 3,
};

function CartsPanel({
  items,
  isLoading,
  isError,
}: {
  items: CartHeader[];
  isLoading: boolean;
  isError: boolean;
}) {
  // Erledigte Warenkörbe sind keine Arbeit mehr; sie bleiben im Warenkorb.
  const active = items
    .filter((cart) => cart.status !== "done")
    .sort(
      (a, b) =>
        CART_STATUS_ORDER[a.status] - CART_STATUS_ORDER[b.status] ||
        b.updated_at.localeCompare(a.updated_at),
    )
    .slice(0, PANEL_ROWS);
  return (
    <Panel title="Meine Warenkörbe" action={{ label: "Zum Warenkorb →", to: "/warenkorb" }}>
      {isLoading ? (
        <LoadingRows rows={3} />
      ) : isError ? (
        <PanelMessage tone="error">Warenkörbe konnten nicht geladen werden.</PanelMessage>
      ) : active.length === 0 ? (
        <PanelMessage>
          Kein offener Warenkorb. Artikel kommen über die Artikelsuche hinein.
        </PanelMessage>
      ) : (
        <ul className="divide-y divide-slate-100">
          {active.map((cart) => (
            <li key={cart.cart_id}>
              <RowLink to={`/warenkorb?cart=${encodeURIComponent(cart.cart_id)}`}>
                <div className="min-w-0">
                  <div className="break-words text-sm font-medium text-slate-900">{cart.name}</div>
                  <div className="mt-0.5 truncate text-xs text-slate-600">
                    {cart.line_count} {cart.line_count === 1 ? "Position" : "Positionen"}
                    {cart.cost_center ? ` · ${cart.cost_center.label}` : ""}
                  </div>
                </div>
                <StatusChip tone={cart.status === "open" ? "slate" : "emerald"}>
                  {CART_STATUS_LABELS[cart.status]}
                </StatusChip>
              </RowLink>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

type UploadStatus = { label: string; tone: ChipTone };

function uploadStatus(job: ImportJob): UploadStatus {
  if (job.interrupted) return { label: "abgebrochen", tone: "rose" };
  if (job.completion_pending) return { label: "wird übernommen", tone: "slate" };
  switch (job.status) {
    case "pending":
    case "parsing":
      return { label: "wird gelesen", tone: "slate" };
    case "review":
      return { label: "wartet auf Prüfung", tone: "sky" };
    case "confirmed":
    case "importing":
      return { label: "wird übernommen", tone: "slate" };
    case "completed":
      return { label: "übernommen", tone: "emerald" };
    case "failed":
      return { label: "fehlgeschlagen", tone: "rose" };
    case "rejected":
      return { label: "verworfen", tone: "slate" };
  }
}

function UploadsPanel({
  items,
  isLoading,
  isError,
  linkToImports,
}: {
  items: ImportJob[];
  isLoading: boolean;
  isError: boolean;
  /** Nur wer die Importe sieht, springt von hier in einen einzelnen Import. */
  linkToImports: boolean;
}) {
  return (
    <Panel
      title="Meine letzten Uploads"
      action={linkToImports ? { label: "Alle Importe →", to: "/imports/verlauf" } : undefined}
    >
      {isLoading ? (
        <LoadingRows rows={3} />
      ) : isError ? (
        <PanelMessage tone="error">Uploads konnten nicht geladen werden.</PanelMessage>
      ) : items.length === 0 ? (
        <PanelMessage>
          Noch nichts hochgeladen. Rechnungen, Angebote und Auftragsbestätigungen
          kommen über Konto › „Belege hochladen“ hinein.
        </PanelMessage>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.slice(0, PANEL_ROWS).map((job) => {
            const status = uploadStatus(job);
            const content = (
              <>
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-slate-900">
                    {safeDisplayName(job.source_filename) || "Upload"}
                  </div>
                  <div className="mt-0.5 truncate text-xs text-slate-600">
                    {[
                      job.doc_type ? importDocTypeLabel(job.doc_type) : null,
                      job.supplier,
                      formatDateTimeShort(job.created_at),
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </div>
                <StatusChip tone={status.tone}>{status.label}</StatusChip>
              </>
            );
            return (
              <li key={job.job_id}>
                {linkToImports ? (
                  <RowLink to={`/imports/verlauf?job=${encodeURIComponent(job.job_id)}`}>{content}</RowLink>
                ) : (
                  <div className="flex items-center justify-between gap-3 py-2.5">{content}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

function RecentlyViewedPanel({ items }: { items: RecentlyViewedEntry[] }) {
  return (
    <Panel title="Zuletzt angesehen">
      {items.length === 0 ? (
        <PanelMessage>Artikel und Belege, die du öffnest, erscheinen hier.</PanelMessage>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.slice(0, PANEL_ROWS).map((entry) => (
            <li key={`${entry.kind}:${entry.key}`}>
              <RowLink to={recentlyViewedHref(entry)}>
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-slate-900">{entry.label}</div>
                  {entry.detail ? (
                    <div className="mt-0.5 truncate text-xs text-slate-600">{entry.detail}</div>
                  ) : null}
                </div>
                <span className="shrink-0 text-xs text-slate-600">
                  {entry.kind === "article" ? "Artikel" : "Beleg"} · {formatDateShort(entry.viewed_at)}
                </span>
              </RowLink>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/** Weiße Karte mit Überschrift und einem Weg zur ganzen Liste (Design-System: Karte). */
function Panel({
  title,
  action,
  children,
}: {
  title: string;
  action?: { label: string; to: string };
  children: ReactNode;
}) {
  return (
    <section aria-label={title} className="min-w-0 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      {/* Kopf deutlich über den Zeilen: größer, enger gesetzt und durch eine Linie getrennt. */}
      <div className="mb-1 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 border-b border-slate-100 pb-3">
        <h2 className="text-lg font-semibold tracking-tight text-slate-900">{title}</h2>
        {action ? (
          <Link to={action.to} className="shrink-0 text-sm font-medium text-emerald-700 hover:underline">
            {action.label}
          </Link>
        ) : null}
      </div>
      {children}
    </section>
  );
}

function RowLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="-mx-2 flex items-center justify-between gap-3 rounded-md px-2 py-2.5 transition hover:bg-slate-50 focus:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
    >
      {children}
    </Link>
  );
}

type ChipTone = "slate" | "emerald" | "sky" | "rose";

// Nur Slate, Emerald, Sky und Rose (Design-System), keine zusätzlichen Akzente.
const CHIP_TONES: Record<ChipTone, string> = {
  slate: "bg-slate-100 text-slate-700",
  emerald: "bg-emerald-100 text-emerald-900",
  sky: "bg-sky-100 text-sky-900",
  rose: "bg-rose-100 text-rose-800",
};

function StatusChip({ tone, children }: { tone: ChipTone; children: ReactNode }) {
  return (
    <span className={`shrink-0 rounded px-1.5 py-0.5 text-xs font-medium ${CHIP_TONES[tone]}`}>
      {children}
    </span>
  );
}

function LoadingRows({ rows }: { rows: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="h-12 animate-pulse rounded-md bg-slate-100" />
      ))}
    </div>
  );
}

function PanelMessage({
  children,
  tone = "muted",
}: {
  children: ReactNode;
  tone?: "muted" | "error";
}) {
  return (
    <div
      className={`rounded-lg px-3 py-5 text-center text-sm ${
        tone === "error" ? "bg-rose-50 text-rose-700" : "bg-slate-50 text-slate-600"
      }`}
    >
      {children}
    </div>
  );
}
